import { Buffer } from 'buffer';

interface Payload {
  [key: string]: any;
}

interface AzureTokenPayload {
  aud: string;
  iss: string;
  iat: number;
  nbf: number;
  exp: number;
  acr: string;
  aio: string;
  amr: string[];
  appid: string;
  appidacr: string;
  email: string;
  family_name: string;
  given_name: string;
  fullname?: string;
  idp: string;
  ipaddr: string;
  name: string;
  oid: string;
  rh: string;
  scp: string;
  sub: string;
  tid: string;
  unique_name: string;
  uti: string;
  ver: string;
}

const AzureTokenPayloadService = {
  getPayloadFromToken: (token: string): Payload | null => {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    let output = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    switch (output.length % 4) {
      case 0:
        break;
      case 2:
        output += '==';
        break;
      case 3:
        output += '=';
        break;
      default:
        throw new Error('Illegal base64url string!');
    }

    const base = Buffer.from(output, 'base64');
    const payload = Buffer.from(base).toString('utf-8');

    try {
      const payloadMap: Payload = JSON.parse(payload);
      if (typeof payloadMap !== 'object' || payloadMap === null) {
        return null;
      }
      return payloadMap;
    } catch (e) {
      return null;
    }
  },

  /**
   * Get the user's full name from a token.
   * - Prefer given_name + family_name
   * - Fallback to name
   * - Fallback to parsing email (before @, split by . or _ and capitalize)
   * - Throw error if none available
   */
  getFullNameOfUser: (token?: string | null): string => {
    if (!token) throw new Error('No token provided');
    const user = AzureTokenPayloadService.getAzureTokenPayload(token);
    // Prefer given_name + family_name
    if (user.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`;
    }
    // Fallback to name
    if (user.name) {
      return user.name;
    }
    // Fallback to parsing email
    if (user.email) {
      const emailName = user.email.split('@')[0];
      // Split by . or _ and capitalize each part
      const parts = emailName
        .split(/[._]/)
        .map((part) =>
          part.length ? part[0].toUpperCase() + part.slice(1) : '',
        );
      if (parts.length) {
        return parts.join(' ');
      }
    }
    throw new Error('No valid name fields in token');
  },

  getAzureTokenPayload: (token: string): AzureTokenPayload => {
    const payload = AzureTokenPayloadService.getPayloadFromToken(token);
    if (payload === null) {
      throw new Error('Invalid token when creating Azure Token Payload');
    }

    try {
      const data: AzureTokenPayload = {
        aud: payload['aud']?.toString() ?? '',
        iss: payload['iss']?.toString() ?? '',
        iat: payload['iat'] ?? 0,
        nbf: payload['nbf'] ?? 0,
        exp: payload['exp'] ?? 0,
        acr: payload['acr']?.toString() ?? '',
        aio: payload['aio']?.toString() ?? '',
        amr: payload['amr']
          ? payload['amr'].map((item: any) => item.toString())
          : [],
        appid: payload['appid']?.toString() ?? '',
        appidacr: payload['appidacr']?.toString() ?? '',
        email: payload['email']?.toString(),
        family_name: payload['family_name']?.toString() ?? '',
        given_name: payload['given_name']?.toString() ?? '',
        idp: payload['idp']?.toString() ?? '',
        ipaddr: payload['ipaddr']?.toString() ?? '',
        name: payload['name']?.toString() ?? '',
        oid: payload['oid']?.toString() ?? '',
        rh: payload['rh']?.toString() ?? '',
        scp: payload['scp']?.toString() ?? '',
        sub: payload['sub']?.toString() ?? '',
        tid: payload['tid']?.toString() ?? '',
        unique_name: payload['unique_name']?.toString() ?? '',
        uti: payload['uti']?.toString() ?? '',
        ver: payload['ver']?.toString() ?? '',
      };
      const fullname = () => {
        if (data.given_name.length && data.family_name.length) {
          return `${data.given_name} ${data.family_name}`;
        } else if (data.name.length) {
          return data.name;
        } else {
          throw new Error('No valid name fields in token');
        }
      };
      data.fullname = fullname();
      return data;
    } catch (e) {
      throw e;
    }
  },
};

export default AzureTokenPayloadService;
